/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import dayjs, { type Dayjs } from '@/lib/dayjs';

const ISO_DATE = 'YYYY-MM-DD';

export type StoreTemporaryClosure = {
  /** The date the store reopens. The store is OPEN on this date. */
  reopenDate: Dayjs;
  /** True when the given day falls strictly before the reopen date. */
  isClosedOn: (date: Dayjs) => boolean;
};

/**
 * Yext marks a temporary closure with `hours.reopenDate`. Returns null whenever
 * there is no active closure — missing, malformed, or already-passed dates all
 * fall back to the store's normal hours rather than blanking out its week.
 */
export function getStoreTemporaryClosure(
  reopenDate: string | undefined,
  storeTimezone?: string,
): StoreTemporaryClosure | null {
  if (!reopenDate) {
    return null;
  }

  const parsedReopenDate = dayjs(reopenDate, ISO_DATE, true);

  if (!parsedReopenDate.isValid()) {
    return null;
  }

  // Compare ISO date strings rather than instants: they sort chronologically,
  // so the result cannot drift with the timezone offset of either side.
  const todayInStore = (storeTimezone ? dayjs().tz(storeTimezone) : dayjs()).format(ISO_DATE);

  if (reopenDate <= todayInStore) {
    return null;
  }

  return {
    reopenDate: parsedReopenDate,
    isClosedOn: (date: Dayjs) => date.format(ISO_DATE) < reopenDate,
  };
}
